import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  toasterApi,
  getFindingRetestUiState,
  shouldDisplayRetestingChip,
  mapRetestStartErrorMessage,
  RETEST_START_ERROR_I18N_KEY,
  type RetestCompletedResult,
} from '@terra/ui-design-system';
import type {
  FindingAuditLog,
  FindingTimelineDto,
} from '@terra/shared-contracts-findings';
import { useAbortFindingRetest } from './api/useAbortFindingRetest';
import { useRequestFindingRetest } from './api/useRequestFindingRetest';

type RefetchFn = () => void | Promise<unknown>;

type UseFindingRetestWorkflowParams = {
  findingId: string;
  targetType?: string | null;
  findingStatus?: string | null;
  retestThreadIds?: string[];
  scheduledRetestAt?: Date | string | null;
  isDetailReady: boolean;
  auditLogs: FindingAuditLog[];
  findingTimeline: FindingTimelineDto | null;
  refetchFinding: RefetchFn;
  refetchFindingTimeline: RefetchFn;
  refetchAuditLogs: RefetchFn;
};

type FindingRetestAbortState = {
  isShowingAbortedChip: boolean;
  isPending: boolean;
  abort: () => Promise<void>;
};

type UseFindingRetestWorkflowResult = {
  isRetesting: boolean;
  retestCompletedResult: RetestCompletedResult;
  isPendingHumanValidation: boolean;
  activeRetestThreadId: string | null | undefined;
  activeRetestTraceId: string | null;
  activeRetestExecutionId: string | null;
  requestRetest: () => Promise<void>;
  retestAbort: FindingRetestAbortState;
};

const ABORTED_CHIP_VISIBLE_MS = 8_000;
const FINDING_NOT_RETESTING_ERROR = 'Finding is not currently being retested';

const isFindingNotRetestingError = (error: unknown) =>
  error instanceof Error && error.message.includes(FINDING_NOT_RETESTING_ERROR);

const showRetestCompletionToast = ({
  result,
  successTitle,
  failTitle,
  failDescription,
}: {
  result: Exclude<RetestCompletedResult, null>;
  successTitle: string;
  failTitle: string;
  failDescription: string;
}) => {
  queueMicrotask(() => {
    if (result === 'success') {
      toasterApi.success({
        title: successTitle,
      });

      return;
    }

    toasterApi.error({
      title: failTitle,
      description: failDescription,
    });
  });
};

export const useFindingRetestWorkflow = ({
  findingId,
  targetType,
  findingStatus,
  retestThreadIds = [],
  scheduledRetestAt,
  isDetailReady,
  auditLogs,
  findingTimeline,
  refetchFinding,
  refetchFindingTimeline,
  refetchAuditLogs,
}: UseFindingRetestWorkflowParams): UseFindingRetestWorkflowResult => {
  const t = useTranslations('designSystem.findings.findingDrawer');
  const { mutateAsync: requestFindingRetest, isPending: isRequestingRetest } =
    useRequestFindingRetest();
  const { mutateAsync: abortFindingRetest, isPending: isAbortPending } =
    useAbortFindingRetest();
  const [hasPendingRetestRequest, setHasPendingRetestRequest] = useState(false);
  const [isShowingAbortedChip, setIsShowingAbortedChip] = useState(false);
  const [isAbortInFlightForFinding, setIsAbortInFlightForFinding] =
    useState(false);
  const [hasLocalAbort, setHasLocalAbort] = useState(false);
  const pendingRetestPreviousThreadIdRef = useRef<string | null>(null);
  const abortedChipTimeoutRef = useRef<number | null>(null);
  const suppressRetestSuccessToastsRef = useRef(false);
  const requestInFlightRef = useRef(false);
  const latestRetestThreadId = retestThreadIds.at(-1) ?? null;
  const skipHumanValidation = targetType === 'EXTERNAL_NETWORK';

  const stableRetestUiState = useMemo(
    () =>
      getFindingRetestUiState({
        findingStatus: isDetailReady ? findingStatus : null,
        auditLogs,
        findingTimeline,
        retestThreadIds,
        scheduledRetestAt,
        skipHumanValidation,
      }),
    [
      auditLogs,
      findingStatus,
      findingTimeline,
      isDetailReady,
      retestThreadIds,
      scheduledRetestAt,
      skipHumanValidation,
    ],
  );

  const retestUiState = useMemo(
    () =>
      getFindingRetestUiState({
        findingStatus: isDetailReady ? findingStatus : null,
        auditLogs,
        findingTimeline,
        retestThreadIds,
        scheduledRetestAt,
        isRequestingRetest: isRequestingRetest || hasPendingRetestRequest,
        skipHumanValidation,
      }),
    [
      auditLogs,
      findingStatus,
      findingTimeline,
      hasPendingRetestRequest,
      isDetailReady,
      isRequestingRetest,
      retestThreadIds,
      scheduledRetestAt,
      skipHumanValidation,
    ],
  );

  const {
    isRetesting,
    retestCompletedResult,
    isPendingHumanValidation,
    activeRetestThreadId,
  } = retestUiState;
  const activeRetestTraceId = stableRetestUiState.activeRetestTraceId;
  const activeRetestExecutionId = stableRetestUiState.activeRetestExecutionId;
  const hasObservedRetestingRef = useRef(false);
  const previousRetestCompletedResultRef = useRef(retestCompletedResult);

  const clearAbortedChipTimeout = useCallback(() => {
    if (abortedChipTimeoutRef.current !== null) {
      window.clearTimeout(abortedChipTimeoutRef.current);
      abortedChipTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    setHasPendingRetestRequest(false);
    pendingRetestPreviousThreadIdRef.current = null;
    hasObservedRetestingRef.current = false;
    previousRetestCompletedResultRef.current = null;
    setIsShowingAbortedChip(false);
    setIsAbortInFlightForFinding(false);
    setHasLocalAbort(false);
    requestInFlightRef.current = false;
    suppressRetestSuccessToastsRef.current = false;
    clearAbortedChipTimeout();
  }, [clearAbortedChipTimeout, findingId]);

  useEffect(() => () => clearAbortedChipTimeout(), [clearAbortedChipTimeout]);

  useEffect(() => {
    if (
      hasPendingRetestRequest &&
      latestRetestThreadId &&
      latestRetestThreadId !== pendingRetestPreviousThreadIdRef.current
    ) {
      setHasPendingRetestRequest(false);
      pendingRetestPreviousThreadIdRef.current = null;

      return;
    }

    if (stableRetestUiState.isRetesting || !isDetailReady) {
      setHasPendingRetestRequest(false);
      pendingRetestPreviousThreadIdRef.current = null;
    }
  }, [
    hasPendingRetestRequest,
    latestRetestThreadId,
    stableRetestUiState.isRetesting,
    isDetailReady,
  ]);

  useEffect(() => {
    if (isRetesting) {
      hasObservedRetestingRef.current = true;
    }
  }, [isRetesting]);

  useEffect(() => {
    const shouldShowCompletionToast =
      retestCompletedResult === 'success' &&
      previousRetestCompletedResultRef.current !== 'success' &&
      hasObservedRetestingRef.current;
    const shouldShowFailureToast =
      retestCompletedResult === 'fail' &&
      previousRetestCompletedResultRef.current !== 'fail' &&
      hasObservedRetestingRef.current;

    if (
      (shouldShowCompletionToast || shouldShowFailureToast) &&
      !suppressRetestSuccessToastsRef.current &&
      !isShowingAbortedChip &&
      !isAbortInFlightForFinding &&
      !hasLocalAbort
    ) {
      showRetestCompletionToast({
        result: shouldShowCompletionToast ? 'success' : 'fail',
        successTitle: skipHumanValidation
          ? t('retest.completedSuccessTitle')
          : t('retest.alertSuccessTitle'),
        failTitle: t('retest.completedFailureTitle'),
        failDescription: t('retest.completedFailureDescription'),
      });
      hasObservedRetestingRef.current = false;
    }

    previousRetestCompletedResultRef.current = retestCompletedResult;
  }, [
    hasLocalAbort,
    isAbortInFlightForFinding,
    isShowingAbortedChip,
    retestCompletedResult,
    skipHumanValidation,
    t,
  ]);

  const showAbortedChip = useCallback(() => {
    setHasLocalAbort(true);
    setIsShowingAbortedChip(true);
    clearAbortedChipTimeout();
    abortedChipTimeoutRef.current = window.setTimeout(() => {
      setIsShowingAbortedChip(false);
      abortedChipTimeoutRef.current = null;
    }, ABORTED_CHIP_VISIBLE_MS);
  }, [clearAbortedChipTimeout]);

  const displayedIsRetesting = shouldDisplayRetestingChip({
    isRetesting,
    isAbortInFlight: isAbortInFlightForFinding,
    isShowingAbortedChip,
    hasLocalAbort,
  });

  useEffect(() => {
    if (!isDetailReady || !displayedIsRetesting) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refetchFinding();
      void refetchFindingTimeline();
      void refetchAuditLogs();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [
    displayedIsRetesting,
    isDetailReady,
    refetchAuditLogs,
    refetchFinding,
    refetchFindingTimeline,
  ]);

  const runAbortRequest = useCallback(async () => {
    const shouldOmitClientThreadId =
      requestInFlightRef.current || hasPendingRetestRequest;

    await abortFindingRetest({
      findingId,
      ...(!shouldOmitClientThreadId && activeRetestThreadId
        ? { threadId: activeRetestThreadId }
        : {}),
      ...(!shouldOmitClientThreadId && activeRetestExecutionId
        ? { executionId: activeRetestExecutionId }
        : {}),
    });
  }, [
    abortFindingRetest,
    activeRetestExecutionId,
    activeRetestThreadId,
    findingId,
    hasPendingRetestRequest,
  ]);

  const requestRetest = useCallback(async () => {
    if (requestInFlightRef.current) {
      return;
    }

    requestInFlightRef.current = true;

    pendingRetestPreviousThreadIdRef.current = latestRetestThreadId;
    setHasLocalAbort(false);
    setHasPendingRetestRequest(true);
    setIsShowingAbortedChip(false);
    suppressRetestSuccessToastsRef.current = false;
    clearAbortedChipTimeout();

    try {
      await requestFindingRetest({ findingId });

      if (suppressRetestSuccessToastsRef.current) {
        setHasPendingRetestRequest(false);
        pendingRetestPreviousThreadIdRef.current = null;

        try {
          await abortFindingRetest({ findingId });
          showAbortedChip();
        } catch (error: unknown) {
          if (!isFindingNotRetestingError(error)) {
            toasterApi.error({
              title: t('retest.abortErrorTitle'),
              description: t('retest.abortErrorDescription'),
            });
          }
        }

        return;
      }

      toasterApi.success({
        title: t('retest.requestedTitle'),
        description: t('retest.requestedDescription'),
      });
    } catch (error) {
      setHasPendingRetestRequest(false);
      pendingRetestPreviousThreadIdRef.current = null;
      const errorKind =
        error instanceof Error
          ? mapRetestStartErrorMessage(error.message)
          : 'generic';

      toasterApi.error({
        title: t('retest.triggerErrorTitle'),
        description: t(RETEST_START_ERROR_I18N_KEY[errorKind]),
      });
    } finally {
      requestInFlightRef.current = false;
    }
  }, [
    abortFindingRetest,
    clearAbortedChipTimeout,
    findingId,
    latestRetestThreadId,
    requestFindingRetest,
    showAbortedChip,
    t,
  ]);

  const abort = useCallback(async () => {
    suppressRetestSuccessToastsRef.current = true;
    setIsAbortInFlightForFinding(true);
    showAbortedChip();
    toasterApi.dismiss();

    try {
      await runAbortRequest();
      setHasPendingRetestRequest(false);
      pendingRetestPreviousThreadIdRef.current = null;
      void refetchFinding();
      void refetchFindingTimeline();
      void refetchAuditLogs();
    } catch (error: unknown) {
      if (requestInFlightRef.current && isFindingNotRetestingError(error)) {
        setHasPendingRetestRequest(false);
        pendingRetestPreviousThreadIdRef.current = null;

        return;
      }

      setHasLocalAbort(false);
      setIsShowingAbortedChip(false);
      clearAbortedChipTimeout();
      suppressRetestSuccessToastsRef.current = false;
      toasterApi.error({
        title: t('retest.abortErrorTitle'),
        description: t('retest.abortErrorDescription'),
      });
    } finally {
      setIsAbortInFlightForFinding(false);
    }
  }, [
    clearAbortedChipTimeout,
    refetchAuditLogs,
    refetchFinding,
    refetchFindingTimeline,
    runAbortRequest,
    showAbortedChip,
    t,
  ]);

  const retestAbort = useMemo(
    (): FindingRetestAbortState => ({
      isShowingAbortedChip,
      isPending: isAbortPending || isAbortInFlightForFinding,
      abort,
    }),
    [abort, isAbortInFlightForFinding, isAbortPending, isShowingAbortedChip],
  );

  return {
    isRetesting: displayedIsRetesting,
    retestCompletedResult,
    isPendingHumanValidation,
    activeRetestThreadId,
    activeRetestTraceId,
    activeRetestExecutionId,
    requestRetest,
    retestAbort,
  };
};
